/**
 * Share one in-flight Promise across concurrent callers.
 *
 * This is not a result cache: once the Promise settles, the next caller starts
 * a new request. Explicit refresh and reconnect remain unblocked after the
 * current request completes. Concurrent callers during the same in-flight
 * window receive the same Promise instead of issuing a duplicate HTTP call.
 */
export const createKeyedInFlightShare = <T>() => {
  const inFlight = new Map<string, Promise<T>>();

  return {
    run(key: string, factory: () => Promise<T>): Promise<T> {
      const existing = inFlight.get(key);
      if (existing) {
        return existing;
      }

      const shared = factory().finally(() => {
        if (inFlight.get(key) === shared) {
          inFlight.delete(key);
        }
      });
      inFlight.set(key, shared);
      return shared;
    },
    reset() {
      inFlight.clear();
    },
  };
};
